import axios from 'axios';
import { reactive } from 'vue';

/*
 * Server-owned page state.
 *
 * Titles, descriptions, canonicals, robots and JSON-LD are decided by
 * SiteController for every URL. On the first load they are already in the
 * HTML; on client-side navigation we fetch the same payload with the
 * X-Page-Fragment header and apply it, so the SPA never invents metadata.
 */
const initial = window.__PAGE__ || {};

export const page = reactive({
    path: initial.path || window.location.pathname,
    meta: initial.meta || null,
    freshness: initial.freshness || null,
    status: initial.status || 200,
});

// HTML of the server-rendered <main> captured before Vue replaces #app, so
// ServerPage can show it on the first load without a second request.
export const initialHtml = (() => {
    const main = document.querySelector('#app main');
    return main ? { path: page.path, html: main.innerHTML } : null;
})();

const cache = new Map();

export function fetchPage(path) {
    if (!cache.has(path)) {
        cache.set(path, axios
            .get(path, { headers: { 'X-Page-Fragment': '1', Accept: 'application/json' } })
            .then(r => r.data)
            .catch(e => e.response?.data || null));
    }
    return cache.get(path);
}

function setTag(selector, create, attr, value) {
    let el = document.head.querySelector(selector);
    if (!el) { el = create(); document.head.appendChild(el); }
    el.setAttribute(attr, value);
}

export function applyMeta(meta) {
    if (!meta) return;
    document.title = meta.title;
    const m = (key, val, attr = 'name') => setTag(`meta[${attr}="${key}"]`, () => { const e = document.createElement('meta'); e.setAttribute(attr, key); return e; }, 'content', val);
    m('description', meta.description);
    m('robots', meta.robots);
    m('og:title', meta.title, 'property');
    m('og:description', meta.description, 'property');
    m('og:url', meta.canonical, 'property');
    m('og:type', meta.ogType, 'property');
    m('og:image', meta.image, 'property');
    m('twitter:title', meta.title);
    m('twitter:description', meta.description);
    m('twitter:image', meta.image);
    setTag('link[rel="canonical"]', () => { const e = document.createElement('link'); e.rel = 'canonical'; return e; }, 'href', meta.canonical);
    const ld = document.head.querySelector('script[data-schema]');
    if (ld && meta.jsonLdDocument) ld.textContent = JSON.stringify(meta.jsonLdDocument);
}

/** Called from router.afterEach for every navigation after the first. */
export async function syncPage(path) {
    const data = await fetchPage(path);
    if (window.location.pathname !== path || !data) return;
    page.path = path;
    page.meta = data.meta || null;
    page.freshness = data.freshness || null;
    page.status = data.status || 200;
    applyMeta(data.meta);
}

/*
 * Serve the API payloads the server already embedded (window.__PAGE__.preload)
 * for the first matching GET, so the first render uses the same data as the
 * server HTML without a second round trip or a loading flash.
 */
export function installPreload(http = axios) {
    const preload = { ...(initial.preload || {}) };
    http.interceptors.request.use(config => {
        const url = config.url || '';
        const plain = (config.method || 'get') === 'get' && !config.params && !url.includes('?');
        if (plain && Object.prototype.hasOwnProperty.call(preload, url)) {
            const data = preload[url];
            delete preload[url];
            config.adapter = () => Promise.resolve({ data, status: 200, statusText: 'OK', headers: {}, config, request: {} });
        }
        return config;
    });
}
